import type { Level } from '#@/levels.ts'
import type { SerializedError } from '#@/normalizeValue.ts'
import type { LogRecord } from '#@/record.ts'

/** Renders one prefix tag. */
export type PrettyStyle = {
  tag: (name: string, colour: Rgb) => string
}

export type EmitPrettyArgs = {
  level: Level
  record: LogRecord
  prefixes: readonly string[]
  style: PrettyStyle
}

/**
 * `[a] [b] message key=value …` for terminals and browser consoles (R10). Prefixes are coloured by a deterministic hash
 * of their text through `style`; caller text is never coloured. A serialised error follows on its own lines.
 */
export function emitPretty({ level, record, prefixes, style }: EmitPrettyArgs): void {
  const { message, logger: _logger, error, ...rest } = record
  // A caller can put any value under `error` (`null`, a string); only a serialised error gets its own lines.
  const serializedError = isSerializedError(error) ? error : undefined
  const fields: Record<string, unknown> =
    serializedError === undefined && error !== undefined ? { ...rest, error } : rest
  const consoleArgs: unknown[] = [prefixes.map((name) => renderTag(name, style)).join(' '), message]
  if (Object.keys(fields).length > 0) {
    consoleArgs.push(renderFields(fields))
  }
  if (serializedError !== undefined) {
    consoleArgs.push(`\n${renderError(serializedError)}`)
  }
  console[level](...consoleArgs)
}

/** 24-bit ANSI background with black text; Miniflare and a TTY render it. */
export const ansiStyle: PrettyStyle = {
  tag: (name, [r, g, b]) => `\u001B[48;2;${r};${g};${b};38;2;0;0;0m ${name} \u001B[0m`,
}

/** `[name]` for a pipe, a file, or a console that renders no colour. */
export const plainStyle: PrettyStyle = {
  tag: (name) => `[${name}]`,
}

/** Returns RGB colours with components from 180 through 240 for black foreground text. Exported for tests. */
export function tagColour(name: string): Rgb {
  let hash = 0
  for (const character of name) {
    hash = (hash << 5) - hash + (character.codePointAt(0) ?? 0)
    hash |= 0
  }
  const positive = Math.abs(hash)
  return [(positive % 61) + 180, ((positive >> 8) % 61) + 180, ((positive >> 16) % 61) + 180]
}

type Rgb = [number, number, number]

/** `\p{Emoji}` also matches `0`-`9`, `#`, and `*`, which are prefix names that need a tag. */
const SINGLE_PICTOGRAPH = /^\p{Extended_Pictographic}$/u

function renderTag(name: string, style: PrettyStyle): string {
  return SINGLE_PICTOGRAPH.test(name) ? name : style.tag(name, tagColour(name))
}

function renderFields(fields: Record<string, unknown>): string {
  return Object.entries(fields)
    .map(([key, value]) => `${key}=${renderValue(value)}`)
    .join(' ')
}

function renderValue(value: unknown): string {
  if (typeof value === 'string') {
    return /\s/u.test(value) || value === '' ? JSON.stringify(value) : value
  }
  return JSON.stringify(value)
}

function renderError(error: SerializedError, indent = '  '): string {
  const heading = `${error.name}: ${error.message}`
  const stack = error.stack ?? heading
  const lines = (stack.startsWith(heading) ? stack : `${heading}\n${stack}`).split('\n')
  const rendered = lines.map((line) => `${indent}${line}`)
  for (const entry of error.errors ?? []) {
    rendered.push(`${indent}error:`, renderMember(entry, `${indent}  `))
  }
  if (error.cause !== undefined) {
    rendered.push(`${indent}caused by:`, renderMember(error.cause, `${indent}  `))
  }
  return rendered.join('\n')
}

/** A `cause` or an `errors` entry: a serialised error renders as one; any other value renders as JSON. */
function renderMember(member: unknown, indent: string): string {
  return isSerializedError(member) ? renderError(member, indent) : `${indent}${JSON.stringify(member)}`
}

/** `name` and `message` strings, and `stack` and `errors` of their declared types when present. */
function isSerializedError(value: unknown): value is SerializedError {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  return (
    'name' in value
    && typeof value.name === 'string'
    && 'message' in value
    && typeof value.message === 'string'
    && (!('stack' in value) || value.stack === undefined || typeof value.stack === 'string')
    && (!('errors' in value) || value.errors === undefined || Array.isArray(value.errors))
  )
}
